import type { RootReducerType } from '@/redux/reducers/RootReducer.types';
import { isArray, isNil } from 'lodash-es';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { type NavigateOptions, type To, useNavigate } from 'react-router';

const useAppNavigation = () => {
  const rootNavigate = useNavigate();
  const { i18n } = useTranslation();

  const supportedLanguages = useSelector(
    (state: RootReducerType) => state.config.appConfig?.languages,
  );

  const navigate = useCallback(
    (route: To | number, options?: NavigateOptions) => {
      if (typeof route === 'string') {
        const localizedRoute = route.replaceAll(':language', i18n.language);
        rootNavigate(localizedRoute, options);
      } else if (typeof route === 'number') {
        rootNavigate(route);
      } else {
        rootNavigate(route, options);
      }
    },
    [rootNavigate, i18n.language],
  );

  const toLocalizedRoute = useCallback(
    (route: string) => {
      if (!isNil(supportedLanguages) && supportedLanguages.length > 0) {
        let notLocalizedYet = true;
        for (const supportedLanguage of supportedLanguages) {
          if (route.startsWith(`/${supportedLanguage}/`)) {
            notLocalizedYet = false;
            break;
          }
        }

        if (notLocalizedYet) {
          return `/${i18n.language}${route}`;
        }
      }

      return route;
    },
    [i18n.language, supportedLanguages],
  );

  const isSameRoute = useCallback((routeA: string, routeB: string) => {
    if (routeA === routeB) {
      return true;
    }

    const routeAMatch = routeA.match(/^\/?(it|en)?\/(q|p)\/(.*)$/);
    const routeBMatch = routeB.match(/^\/?(it|en)?\/(q|p)\/(.*)$/);

    if (!isArray(routeAMatch) || !isArray(routeBMatch)) {
      return false;
    }

    const aPath = routeAMatch[routeAMatch.length - 1];
    const bPath = routeBMatch[routeBMatch.length - 1];

    return aPath === bPath;
  }, []);

  const isParentRoute = useCallback((routeA: string, routeB: string) => {
    if (routeA !== routeB && routeB.startsWith(routeA)) {
      return true;
    }

    const routeAMatch = routeA.match(/^\/?(it|en)?\/(q|p)\/(.*)$/);
    const routeBMatch = routeB.match(/^\/?(it|en)?\/(q|p)\/(.*)$/);

    if (!isArray(routeAMatch) || !isArray(routeBMatch)) {
      return false;
    }

    const aPath = routeAMatch[routeAMatch.length - 1];
    const bPath = routeBMatch[routeBMatch.length - 1];

    return aPath !== bPath && bPath.startsWith(aPath);
  }, []);

  const isChildRoute = useCallback(
    (routeA: string, routeB: string) => {
      return isParentRoute(routeB, routeA);
    },
    [isParentRoute],
  );

  const getCanonical = useCallback(
    (route: string) => {
      if (!isNil(supportedLanguages) && supportedLanguages.length > 0) {
        let processedRoute = undefined;
        for (const supportedLanguage of supportedLanguages) {
          if (route.startsWith(`/${supportedLanguage}/`)) {
            processedRoute = route.replaceAll(`/${supportedLanguage}/`, '/');
            break;
          }
        }

        if (!isNil(processedRoute)) {
          return processedRoute;
        }
      }

      return route;
    },
    [supportedLanguages],
  );

  return {
    navigate,
    toLocalizedRoute,
    isSameRoute,
    isParentRoute,
    isChildRoute,
    getCanonical,
  };
};

export default useAppNavigation;
